import {
  ChangeEvent,
  KeyboardEventHandler,
  Ref,
  RefCallback,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useCombobox,
  UseComboboxSelectedItemChange,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  UseComboboxStateChangeTypes,
} from 'downshift';

import {
  type BackwardsCompatibleMutableRefObject,
  mergeRefs,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import {
  areItemsEqual,
  checkReferenceEquality,
  ComparableItem,
  createDefaultGetItemKey,
  defaultIsItemDisabled,
  defaultLabelGetter,
  getAriaProps,
  getSingleSelectionHandlers,
  isGroupHeader,
  safeScrollIntoView,
  wrapGetItemProps,
} from '../helpers';
import {
  AutocompleteGetMenuProps,
  GenericItem,
  GetInputProps,
  GetItemProps,
  GetToggleButtonProps,
  GroupHeader,
  InputItemGroup,
  UseAutocompleteProps,
} from '../types';
import { useGroupFilter } from './index';
import useCollapsibleGroups from './useCollapsibleGroups';
import useDropdownId from './useDropdownId';
import useFilteredItems from './useFilteredItems';
import useGroupedItems from './useGroupedItems';
import useIMEComposition from './useIMEComposition';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import useVirtualProps from './useVirtualProps';

const CLEARS_ON_CLOSE: Set<UseComboboxStateChangeTypes> = new Set([
  useCombobox.stateChangeTypes.InputBlur,
  useCombobox.stateChangeTypes.ToggleButtonClick,
  useCombobox.stateChangeTypes.FunctionCloseMenu,
]);

/**
 * A hook used to manage all logic needed for an Autocomplete (searchable) dropdown.
 */
const useAutocomplete = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement,
>({
  id,
  items,
  groupedItems,
  itemToString = defaultLabelGetter,
  defaultHighlightedIndex = 0,
  target,
  overlayRef: overlayRefProp,
  onInputValueChange: onInputValueChangeProp,
  onIsOpenChange: onIsOpenChangeProp,
  layoutDependency,
  getFilteredItems,
  getMenuProps: getMenuPropsProp,
  initialInputValue,
  stateReducer: stateReducerProp,
  inputRef: inputRefProp,
  isItemDisabled = defaultIsItemDisabled,
  inputValue: inputValueProp,
  enableSearchHighlight,
  itemToKey,
  getItemKey: getItemKeyProp,
  onItemSelect: onItemSelectProp,
  onSelectedItemChange: onSelectedItemChangeProp,
  preserveItemOrder,
  ...props
}: UseAutocompleteProps<TItem, TTarget>) => {
  const getItemKey = useMemo(
    () => getItemKeyProp || createDefaultGetItemKey(itemToString),
    [getItemKeyProp, itemToString],
  );

  const compareItems = useCallback(
    (item1: ComparableItem<TItem>, item2: ComparableItem<TItem>) =>
      areItemsEqual(
        item1,
        item2,
        itemToKey as
          | ((item: ComparableItem<TItem>) => string | undefined)
          | undefined,
      ),
    [itemToKey],
  );

  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality({ ...props, items: maybeGroupedItems, itemToKey });

  const ariaProps = getAriaProps(props);
  const {
    allowCollapsibleGroups,
    showGroupItemCount,
    allowGroupFiltering,
    showEmptyGroups,
  } = props;

  const dropdownId = useDropdownId(id);
  const inputRef = useForwardedRef(inputRefProp || null);
  const targetRef = useForwardedRef(target || null);
  const overlayRef = useForwardedRef(overlayRefProp || null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string>('group0');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>();
  const [highlightedMenuIndex, setHighlightedMenuIndex] = useState(-1);

  const {
    handleCompositionStart,
    getOnCompositionEnd,
    onInputValueChange,
    inputValue,
    setInputValue,
  } = useIMEComposition({
    inputValueProp,
    initialInputValue,
    onInputValueChangeProp,
  });

  const { flatItems, getMenuItems, isGrouped, getAllGroupHeaders } =
    useGroupedItems({
      maybeGroupedItems,
      allowCollapsibleGroups,
      expandedGroupKey,
      selectedGroupKey,
      showEmptyGroups,
    });

  const shouldPreventInitialFiltering =
    props.initialSelectedItem &&
    initialInputValue === itemToString(props.initialSelectedItem);

  const shouldPreventInitialFilteringWithSelectedItem =
    props.initialSelectedItem && !initialInputValue;

  const [isFilteringActive, setIsFilteringActive] = useState(
    Boolean(initialInputValue) &&
      !shouldPreventInitialFiltering &&
      !shouldPreventInitialFilteringWithSelectedItem,
  );

  const { filteredItems } = useFilteredItems<TItem>({
    items: flatItems,
    getFilteredItems,
    itemToString,
    inputValue: isFilteringActive ? inputValue : '',
  });

  const { filteredGroupItems, showGroupHeaders } = useGroupFilter({
    items: filteredItems,
    maybeGroupedItems,
    selectedGroupKey,
    getItemKey,
  });

  const setSelectedGroupKeyWithExpand = useCallback(
    (key: string | undefined) => {
      setSelectedGroupKey(key);
      if (allowCollapsibleGroups) {
        setExpandedGroupKey(key !== undefined && key !== '' ? key : 'group0');
      }
    },
    [allowCollapsibleGroups],
  );

  const selectedItemRef = useRef<TItem | null>(
    props.selectedItem ?? props.initialSelectedItem ?? null,
  );

  // Controlled: use prop directly (null = explicit clear, defined = controlled value).
  // Uncontrolled (undefined): fall back to the ref so the memo still has a stable value.
  const currentSelectedItem =
    props.selectedItem === null
      ? null
      : (props.selectedItem ?? selectedItemRef.current);

  const baseVisibleItems =
    isFilteringActive || selectedGroupKey ? filteredGroupItems : flatItems;

  const visibleItems = useMemo(() => {
    if (!currentSelectedItem) {
      return baseVisibleItems;
    }

    const selectedIndex = baseVisibleItems.findIndex(
      (item) => !isGroupHeader(item) && compareItems(item, currentSelectedItem),
    );

    if (selectedIndex !== -1) {
      return baseVisibleItems;
    }

    if (selectedGroupKey || isGrouped) {
      return baseVisibleItems;
    }

    return [currentSelectedItem, ...baseVisibleItems];
  }, [
    baseVisibleItems,
    selectedGroupKey,
    isGrouped,
    compareItems,
    currentSelectedItem,
  ]);

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const handleEnterKeyDown = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      const isNoItemSelected =
        !changes.selectedItem ||
        changes.highlightedIndex === undefined ||
        changes.highlightedIndex < 0;

      if (isNoItemSelected) {
        const hasAnyVisibleItem = visibleItems.length > 0;
        const hasAnySelectableItem = visibleItems.some((item, idx) => {
          if (isGroupHeader(item)) return false;
          return !isItemDisabled(item as TItem, idx);
        });

        if (hasAnyVisibleItem && !hasAnySelectableItem) {
          return state;
        }

        if (
          state.highlightedIndex !== undefined &&
          state.highlightedIndex >= 0
        ) {
          const highlighted = visibleItems[state.highlightedIndex];
          if (
            highlighted &&
            isItemDisabled(highlighted as TItem, state.highlightedIndex)
          ) {
            return state;
          }
        }
        setIsFilteringActive(false);
        return {
          ...changes,
          isOpen: false,
          inputValue: itemToString(state.selectedItem),
        };
      }

      return changes;
    },
    [isItemDisabled, itemToString, visibleItems],
  );

  const handleInputClick = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (!state.isOpen) {
        return changes;
      }

      const selectedItemText = state.selectedItem
        ? itemToString(state.selectedItem)
        : '';
      const shouldActivateFiltering =
        inputValue.length > 0 && inputValue !== selectedItemText;
      setIsFilteringActive(shouldActivateFiltering);

      return {
        ...changes,
        highlightedIndex: state.highlightedIndex,
        isOpen: true,
      };
    },
    [inputValue, itemToString],
  );

  const handleInputChange = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (changes.inputValue === undefined) {
        return changes;
      }

      setIsFilteringActive(true);

      const isSelectAllAndType =
        changes.inputValue.length === 1 && state.inputValue.length > 2;

      if (isSelectAllAndType) {
        return { ...changes, selectedItem: null };
      }

      return changes;
    },
    [],
  );

  const handleOpenMenu = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      const shouldHighlightSelectedItem = changes.isOpen && state.selectedItem;

      if (shouldHighlightSelectedItem && visibleItems.length === 0) {
        return { ...changes, inputValue: '' };
      }

      if (shouldHighlightSelectedItem && state.selectedItem) {
        const selectedIndex = visibleItems.findIndex(
          (item) =>
            !isGroupHeader(item) && compareItems(item, state.selectedItem),
        );
        let finalIndex = Math.max(selectedIndex, 0);
        if (finalIndex === 0 && isGroupHeader(visibleItems[0])) {
          const firstNonHeaderIndex = visibleItems.findIndex(
            (item) => !isGroupHeader(item),
          );
          finalIndex = Math.max(firstNonHeaderIndex, 0);
        }
        return {
          ...changes,
          highlightedIndex: finalIndex,
          inputValue: '',
        };
      }

      return changes;
    },
    [visibleItems, compareItems],
  );

  const handleEscapeKey = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      setIsFilteringActive(false);

      return {
        ...changes,
        isOpen: false,
        inputValue: itemToString(state.selectedItem),
      };
    },
    [itemToString],
  );

  const handleInputBlur = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      const shouldKeepMenuOpen = getShouldKeepMenuOpenOnBlur();

      if (!shouldKeepMenuOpen) {
        setIsFilteringActive(false);
      }

      return {
        ...changes,
        isOpen: shouldKeepMenuOpen,
        inputValue: itemToString(state.selectedItem),
      };
    },
    [getShouldKeepMenuOpenOnBlur, itemToString],
  );

  const handleItemSelection = useCallback(
    (
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (changes.selectedItem) {
        const isSpecificMultiSelectFiltering =
          stateReducerProp &&
          inputValue &&
          inputValue.length <= 2 &&
          inputValue !== itemToString(changes.selectedItem);

        if (!isSpecificMultiSelectFiltering) {
          setIsFilteringActive(false);
        }
      }
      return changes;
    },
    [stateReducerProp, inputValue, itemToString],
  );

  const handleMenuClose = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (changes.isOpen) {
        return changes;
      }

      setIsFilteringActive(false);

      return {
        ...changes,
        inputValue: itemToString(state.selectedItem),
        // Downshift: reset index so on reopen it scrolls to selected item (downshift#645)
        highlightedIndex: -1,
      };
    },
    [itemToString],
  );

  const autocompleteStateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      { changes, type }: UseComboboxStateChangeOptions<TItem>,
    ): Partial<UseComboboxState<TItem>> => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          return handleEnterKeyDown(state, changes);
        case useCombobox.stateChangeTypes.InputClick:
          return handleInputClick(state, changes);
        case useCombobox.stateChangeTypes.InputChange:
          return handleInputChange(state, changes);
        case useCombobox.stateChangeTypes.FunctionOpenMenu:
          return handleOpenMenu(state, changes);
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          return handleEscapeKey(state, changes);
        case useCombobox.stateChangeTypes.InputBlur:
          return handleInputBlur(state, changes);
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.FunctionSelectItem:
          return handleItemSelection(changes);
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
          return handleMenuClose(state, changes);
        default:
          return changes;
      }
    },
    [
      handleEnterKeyDown,
      handleInputClick,
      handleInputChange,
      handleOpenMenu,
      handleEscapeKey,
      handleInputBlur,
      handleItemSelection,
      handleMenuClose,
    ],
  );

  const onIsOpenChange = useCallback(
    (args: UseComboboxStateChange<TItem>) => {
      if (!args.isOpen && !args.selectedItem) {
        const closeType = args.type;
        const shouldClearOnClose = CLEARS_ON_CLOSE.has(closeType);

        if (shouldClearOnClose) {
          setInputValue('');
        }
      }

      onIsOpenChangeProp?.(args);
    },
    [onIsOpenChangeProp, setInputValue],
  );

  const stateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      actionAndChanges: UseComboboxStateChangeOptions<TItem>,
    ) => {
      const updatedState = autocompleteStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [autocompleteStateReducer, stateReducerProp],
  );

  const wrappedIsItemDisabled = useCallback(
    (item: TItem | undefined, index: number) => {
      if (item === undefined) {
        return true;
      }
      return isItemDisabled(item, index);
    },
    [isItemDisabled],
  );

  const onSelectedItemChange = useCallback(
    (changes: UseComboboxSelectedItemChange<TItem>) => {
      onSelectedItemChangeProp?.(changes);

      const { type, selectedItem: item } = changes;
      if (
        onItemSelectProp &&
        item &&
        (type === useCombobox.stateChangeTypes.ItemClick ||
          type === useCombobox.stateChangeTypes.InputKeyDownEnter)
      ) {
        onItemSelectProp(item, {
          action: 'select',
          selectedItem: item,
          selectedItems: [item],
        });
      }
    },
    [onItemSelectProp, onSelectedItemChangeProp],
  );

  const {
    isOpen: downshiftIsMenuOpen,
    getInputProps: rawGetInputProps,
    getItemProps: rawGetItemProps,
    getToggleButtonProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    openMenu,
    highlightedIndex,
    selectedItem,
    ...useComboboxReturnValue
  } = useCombobox<TItem>({
    id: dropdownId,
    items: visibleItems,
    itemToString,
    ...(itemToKey && { itemToKey }),
    initialInputValue,
    onInputValueChange,
    onIsOpenChange,
    defaultHighlightedIndex,
    stateReducer,
    isItemDisabled: wrappedIsItemDisabled,
    scrollIntoView: safeScrollIntoView,
    ...props,
    onSelectedItemChange,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    onHide: useComboboxReturnValue.closeMenu,
    target: targetRef,
    overlayRef,
    layoutDependency,
    isOverlayConditionallyRendered: false,
    ...props,
  });

  useEffect(() => {
    if (selectedItem !== undefined) {
      selectedItemRef.current = selectedItem;
    }
  }, [selectedItem]);

  useEffect(() => {
    if (downshiftIsMenuOpen && selectedItem && inputValue === '') {
      setIsFilteringActive(false);
    }
  }, [downshiftIsMenuOpen, selectedItem, inputValue]);

  useEffect(() => {
    if (!useOverlayIsOpen) {
      setHighlightedMenuIndex(-1);
    }
  }, [useOverlayIsOpen]);

  const getMenuProps: AutocompleteGetMenuProps = useCallback(
    (options, otherOptions) => {
      return getMenuPropsProp
        ? rawGetMenuProps(
            getMenuPropsProp({ ...overlayProps, ...options }, otherOptions),
            otherOptions,
          )
        : rawGetMenuProps({ ...overlayProps, ...options }, otherOptions);
    },
    [getMenuPropsProp, overlayProps, rawGetMenuProps],
  ) as AutocompleteGetMenuProps;

  const getInputProps = useCallback<GetInputProps>(
    ({ ref: refProp, onKeyDown: onKeyDownProp, ...options } = {}) => {
      const combinedRef: RefCallback<HTMLInputElement> = (element) => {
        if (!element) {
          return;
        }

        if (!refProp || typeof refProp === 'string') {
          /* empty */
        } else if (typeof refProp === 'function') {
          refProp(element);
        } else if (typeof refProp === 'object') {
          (
            refProp as BackwardsCompatibleMutableRefObject<HTMLInputElement>
          ).current = element;
        }

        inputRef.current = element;
      };

      const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        controlOnKeyDown(e);

        onKeyDownProp?.(e);
      };

      const {
        onChange: rawOnChange,
        ref,
        ...inputProps
      } = rawGetInputProps({
        ...options,
        onKeyDown,
        ref: combinedRef,
      });

      const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event?.target?.value);
        rawOnChange?.(event);
      };

      const {
        'aria-controls': ariaControls,
        'aria-labelledby': _ariaLabelledBy,
        ...restInputProps
      } = inputProps;

      return {
        onChange,
        onCompositionStart: handleCompositionStart,
        onCompositionEnd: getOnCompositionEnd({ onChangeProp: rawOnChange }),
        ref: ref as Ref<HTMLInputElement>,
        ...restInputProps,
        ...(useOverlayIsOpen && ariaControls
          ? { 'aria-controls': ariaControls }
          : {}),
      };
    },
    [
      controlOnKeyDown,
      getOnCompositionEnd,
      handleCompositionStart,
      inputRef,
      rawGetInputProps,
      setInputValue,
      useOverlayIsOpen,
    ],
  );

  type MenuItems = typeof groupedItems extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];

  const menuItems = useMemo(() => {
    getMenuItems(filteredItems);
    const items = getMenuItems(visibleItems, false) as MenuItems;

    if (
      !preserveItemOrder &&
      selectedItem &&
      downshiftIsMenuOpen &&
      !selectedGroupKey &&
      !isGrouped
    ) {
      const selectedIndex = items.findIndex((item) => {
        if (isGroupHeader(item)) return false;
        return compareItems(item, selectedItem);
      });

      if (selectedIndex > 0) {
        const reorderedItems = [...items];
        const [selected] = reorderedItems.splice(selectedIndex, 1);
        reorderedItems.unshift(selected);
        return reorderedItems;
      }

      if (selectedIndex === -1) {
        return [selectedItem, ...items];
      }
    }

    return items;
  }, [
    getMenuItems,
    filteredItems,
    visibleItems,
    selectedItem,
    downshiftIsMenuOpen,
    selectedGroupKey,
    isGrouped,
    compareItems,
    preserveItemOrder,
  ]);

  const allGroupHeadersMenuItems = useMemo(() => {
    if (!isGrouped || !getAllGroupHeaders) {
      return menuItems;
    }
    // @ts-expect-error -- TS cannot infer the correct type here
    return [...getAllGroupHeaders] as MenuItems;
  }, [isGrouped, getAllGroupHeaders, menuItems]);

  const defaultIndexGetter = useCallback(
    (item: TItem, indexHint?: number) => {
      // When we have a menu position hint and grouped items, compute the exact
      // visibleItems index by counting non-header items before the hint in
      // menuItems. This handles duplicate items (same ref shared between pinned
      // and regular groups) correctly even when they're adjacent in visibleItems.
      if (
        isGrouped &&
        indexHint !== undefined &&
        indexHint >= 0 &&
        indexHint < menuItems.length
      ) {
        let nonHeaderCount = 0;
        for (let i = 0; i < indexHint; i++) {
          if (!isGroupHeader(menuItems[i])) {
            nonHeaderCount++;
          }
        }
        if (
          nonHeaderCount < visibleItems.length &&
          compareItems(visibleItems[nonHeaderCount], item)
        ) {
          return nonHeaderCount;
        }
      }

      // Fallback for non-grouped items or when hint doesn't resolve
      return visibleItems.findIndex(
        (visibleItem) =>
          !isGroupHeader(visibleItem) && compareItems(visibleItem, item),
      );
    },
    [visibleItems, menuItems, isGrouped, compareItems],
  );

  const getItemProps: GetItemProps<TItem> = useCallback(
    (args) =>
      wrapGetItemProps({
        getItemProps: rawGetItemProps,
        highlightedIndex,
        selectedItem,
        indexGetter: defaultIndexGetter,
        getItemKey,
      })(args),
    [
      defaultIndexGetter,
      getItemKey,
      highlightedIndex,
      rawGetItemProps,
      selectedItem,
    ],
  );

  const getItemPropsForMenu: GetItemProps<TItem> = useCallback(
    (args) => {
      const result = getItemProps(args);
      if (
        allowCollapsibleGroups &&
        typeof args.index === 'number' &&
        args.index >= 0
      ) {
        const menuIndex = args.index;
        const resultWithOptionalHandlers = result as typeof result & {
          onMouseEnter?: (e: React.MouseEvent) => void;
        };
        const existingOnMouseEnter = resultWithOptionalHandlers.onMouseEnter;
        return {
          ...result,
          onMouseEnter: (e: React.MouseEvent) => {
            setHighlightedMenuIndex(menuIndex);
            existingOnMouseEnter?.(e);
          },
        };
      }
      return result;
    },
    [allowCollapsibleGroups, getItemProps],
  );

  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);

  const virtualProps = useVirtualProps({
    menuItems,
    isGroupHeader,
    showEmptyGroups,
    showGroupHeaders,
  });

  const singleSelectionHandlers = getSingleSelectionHandlers({
    id: dropdownId,
    selectItem,
    closeMenu: useComboboxReturnValue.closeMenu,
    refToFocusOnClear: inputRef,
    selectedItem,
  });

  const effectiveHighlightedIndexForGroups =
    allowCollapsibleGroups && highlightedMenuIndex >= 0
      ? highlightedMenuIndex
      : highlightedIndex;

  const { setExpandedGroup } = useCollapsibleGroups({
    allowCollapsibleGroups,
    maybeGroupedItems,
    highlightedIndex: effectiveHighlightedIndexForGroups,
    menuItems,
    expandedGroupKey,
    setExpandedGroupKey,
  });

  return {
    controlProps: {
      ref: targetRef,
      isAutocomplete: true as const,
      isOpen: useOverlayIsOpen,
      getInputProps,
      getToggleButtonProps: getToggleButtonProps as GetToggleButtonProps,
      openMenu,
      selectedItem,
      itemToString,
      selectItem: selectItem as (item: TItem | null) => void,
      ...(props['data-testid'] ? { 'data-testid': props['data-testid'] } : {}),
      ...ariaProps,
    },
    menuProps: {
      allowCollapsibleGroups,
      allowGroupFiltering,
      dropdownId,
      enableSearchHighlight,
      expandedGroupKey,
      getItemProps: allowCollapsibleGroups ? getItemPropsForMenu : getItemProps,
      getItemKey,
      getMenuProps,
      groupedItems: maybeGroupedItems,
      highlightedIndex,
      inputValue,
      isAutocomplete: true,
      isGroupHeader,
      isOpen: useOverlayIsOpen,
      menuItems,
      allGroupHeadersMenuItems,
      refToFocusOnClick: inputRef,
      selectedGroupKey,
      selectedItem,
      selectItem,
      setExpandedGroupKey: setExpandedGroup,
      setHighlightedIndex: useComboboxReturnValue.setHighlightedIndex,
      setSelectedGroupKey: setSelectedGroupKeyWithExpand,
      showEmptyGroups,
      showGroupHeaders,
      showGroupItemCount,
      width: targetRef.current?.offsetWidth,
      ...singleSelectionHandlers.menuProps,
    },
    /**
     * Props to be provided to `@tanstack/react-virtual`'s `useVirtual` hook when using virtualization.
     */
    virtualProps,
    selectItem,
    ...useComboboxReturnValue,
  };
};

export default Object.assign(useAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes,
});
